import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import style from '../partners.modules.scss'
import { Table } from 'components/core-ui/Table'
import { useNavigate } from 'react-router-dom'
import {
  useExternalTypesQuery,
  useManagersQuery,
  usePartnersQuery,
  useStatusAssignmentQuery
} from '../api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { columns, getFilters, toolbarActions } from './helpers'
import rowActions from '../helpers'
import EditPartnerModal from './EditPartner/EditPartnerModal'
import { DeleteModal } from './components/DeleteModal'
import { Link } from 'components/core-ui/Link'
import { NewAssignmentModal } from '../common/NewAssignmentModal'

const Partners: React.FC = () => {
  const [modalState, setModalState] = useState({
    isCreateModalOpen: false,
    isEditModalOpen: false,
    isDisableModalOpen: false,
    selectedRowId: null
  })
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [externalPartnerTypeOptions, setExternalPartnerTypeOptions] = useState<
    AutocompleteOption[]
  >([])
  const [managersOptions, setManagersOptions] = useState<AutocompleteOption[]>([])
  const [statusOptions, setStatusOptions] = useState<AutocompleteOption[]>([])

  const { canCreate, canEdit, canDelete } = { canCreate: true, canEdit: true, canDelete: false }

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const { data: managersData } = useManagersQuery(
    showNotification,
    '/iam/external-partners/summaries/managers-slim'
  )

  useEffect(() => {
    if (managersData) {
      setManagersOptions(
        managersData.map(manager => ({
          label: manager.fullName,
          value: manager.id
        })) ?? []
      )
    }
  }, [managersData])

  const { data: statusData, isLoading: statusLoading } = useStatusAssignmentQuery(
    showNotification,
    '/iam/external-partners/statuses'
  )

  useEffect(() => {
    if (statusData) {
      setStatusOptions(
        statusData.list?.filter(s => s.isEnabled).map(s => ({ label: s.name, value: s.name })) ?? []
      )
    }
  }, [statusData])

  const openCreateModal = useCallback(
    (jobId?: number) => {
      setModalState({ ...modalState, isCreateModalOpen: true, selectedRowId: jobId })
    },
    [modalState]
  )

  const closeCreateModal = useCallback(() => {
    setModalState({ ...modalState, isCreateModalOpen: false, selectedRowId: null })
    refetch()
  }, [modalState])

  const openEditModal = useCallback(
    (jobId?: number) => {
      setModalState({ ...modalState, isEditModalOpen: true, selectedRowId: jobId })
    },
    [modalState]
  )

  const closeEditModal = useCallback(() => {
    setModalState({ ...modalState, isEditModalOpen: false, selectedRowId: null })
    refetch()
  }, [modalState])

  const openDisableModal = useCallback(
    (jobId: number) => {
      setModalState({ ...modalState, isDisableModalOpen: true, selectedRowId: jobId })
    },
    [modalState]
  )

  const closeDisableModal = useCallback(() => {
    setModalState({ ...modalState, isDisableModalOpen: false, selectedRowId: null })
    refetch()
  }, [modalState])

  const selectRow = useCallback(
    (rowId: number, action: string) => {
      action === 'edit' ? openEditModal(rowId) : openDisableModal(rowId)
    },
    [openEditModal, openDisableModal]
  )

  const [fetchParams, setFetchParams] = useState({
    page: 0,
    rowsPerPage: 10,
    order: 'desc',
    orderBy: 'id',
    tableFilters: [],
    searchText: ''
  })

  const {
    data: jobsData,
    isLoading,
    refetch
  } = usePartnersQuery(
    showNotification,
    toTableGETUrl({
      ...fetchParams,
      url: '/iam/external-partners/people',
      searchFields: ['personName', 'email', 'globalId', 'marca', 'managerName'],
      searchText: fetchParams.searchText
    })
  )

  const handleRemoteData = useCallback(
    ({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
      setFetchParams({ page, rowsPerPage, order, orderBy, tableFilters, searchText })
    },
    []
  )

  const toolbar = useMemo(
    () => toolbarActions({ canCreate, navigate, openModal: openCreateModal }),
    [canCreate, navigate, openCreateModal]
  )

  const { data: externalTypesData, isLoading: externalTypesLoading } = useExternalTypesQuery(
    showNotification,
    '/iam/external-partners/external-partner-types'
  )

  useEffect(() => {
    if (externalTypesData) {
      setExternalPartnerTypeOptions(
        externalTypesData.list
          ?.filter(s => !s.name.toLowerCase().endsWith('_old'))
          .map(s => ({
            label: s.name,
            value: s.id,
            extensionPeriod: s.extensionPeriod,
            group: s.group
          })) ?? []
      )
    }
  }, [externalTypesData])

  const filters = useMemo(
    () =>
      getFilters(
        externalTypesData?.list.map(type => ({ label: type.name, value: type.id })) || [],
        managersOptions,
        statusOptions
      ),
    [externalTypesData, managersOptions, statusOptions]
  )

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>Partners</span>
      </div>
      {canCreate && (
        <NewAssignmentModal
          isModalOpen={modalState.isCreateModalOpen}
          closeModal={closeCreateModal}
          externalPartnerTypeOptions={externalPartnerTypeOptions}
          isPartnerCreation
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <EditPartnerModal
          isModalOpen={modalState.isEditModalOpen}
          onModalClose={closeEditModal}
          partnerId={modalState.selectedRowId}
          onSuccess={() => setShouldRefreshData(true)}
        />
      )}
      {canDelete && (
        <DeleteModal
          isModalOpen={modalState.isDisableModalOpen}
          closeModal={closeDisableModal}
          itemId={modalState.selectedRowId}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <Box>
        <Table
          hideCheckbox
          hasSearchInput
          orderBy={fetchParams.orderBy}
          order={fetchParams.order}
          columns={columns}
          items={jobsData?.list || []}
          isLoading={isLoading}
          totalCount={jobsData?.page.totalCount || fetchParams.page}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          rowsPerPage={fetchParams.rowsPerPage}
          shouldResetPage={false}
          fetchData={handleRemoteData}
          rowActions={rowActions({
            canEdit,
            canDelete,
            navigate,
            selectRow,
            detailsUrl: '/iam/externalPartners/partner'
          })}
          rowActionsWidth="130px"
          desktopRowActionsWidth="100px"
          toolbarActions={toolbar}
          filters={filters}
        />
      </Box>
    </div>
  )
}

export default Partners
